import { useMemo } from 'react';
import { useTranslation } from '@doccareplus/shared/lib';
import { useClinicAccessQuery } from '@features/clinic-access';
import {
  useAvailabilityQuery, useClinicProfileQuery, useClinicSettingsQuery,
  useIntakeFormQuery,
  useRoomsQuery, useServicesQuery,
} from '../application/settings.query';
import {
  useAddRoomCommand, useAddServiceCommand, useReplaceAvailabilityCommand,
  useCancelLeaveCommand, useCreateLeaveCommand, useDecideLeaveCommand,
  useUpdateClinicProfileCommand, useUpdateClinicSettingsCommand, useUpdateRoomCommand,
  useUpdateServiceCommand,
} from '../application/settings.command';
import { ClinicSettingsWorkspace, type ClinicSection } from './shared/ClinicSettingsWorkspace';
import { ClinicSettingsPageSkeleton } from './SettingsSkeletons';

export function ClinicSettingsPage() {
  const { t } = useTranslation('settings');
  const access = useClinicAccessQuery();
  const permissions = access.data?.permissions ?? [];
  const canManageClinic = permissions.includes('clinic.settings.manage');
  const canManageServices = permissions.includes('clinic.services.manage');
  const canManageRooms = permissions.includes('clinic.rooms.manage');
  const canManageAvailability = permissions.includes('clinic.availability.manage');
  const canManageOwnAvailability = permissions.includes('clinic.availability.own');

  // Roles & Permissions is its own admin destination (/staff/roles) — no longer a settings tab.
  const sections = useMemo<ClinicSection[]>(() => [
    ...(canManageClinic ? ['profile' as const] : []),
    ...(canManageServices || canManageRooms ? ['services' as const] : []),
    ...(canManageAvailability || canManageOwnAvailability ? ['availability' as const] : []),
    ...(canManageClinic ? ['intake' as const, 'reminders' as const, 'security' as const] : []),
  ], [canManageAvailability, canManageClinic, canManageOwnAvailability, canManageRooms, canManageServices]);

  const profile = useClinicProfileQuery(canManageClinic);
  const clinicSettings = useClinicSettingsQuery(canManageClinic);
  const intakeForm = useIntakeFormQuery(canManageClinic);
  const services = useServicesQuery(canManageServices);
  const rooms = useRoomsQuery(canManageRooms);
  const availability = useAvailabilityQuery(canManageAvailability || canManageOwnAvailability);

  const updateProfile = useUpdateClinicProfileCommand();
  const updateSettings = useUpdateClinicSettingsCommand();
  const addService = useAddServiceCommand();
  const updateService = useUpdateServiceCommand();
  const addRoom = useAddRoomCommand();
  const updateRoom = useUpdateRoomCommand();
  const replaceAvailability = useReplaceAvailabilityCommand();
  const createLeave = useCreateLeaveCommand();
  const decideLeave = useDecideLeaveCommand();
  const cancelLeave = useCancelLeaveCommand();

  if (access.isLoading || (canManageClinic && (profile.isLoading || clinicSettings.isLoading || intakeForm.isLoading))) {
    return <ClinicSettingsPageSkeleton label={t('clinicTitle')} />;
  }

  return (
    <ClinicSettingsWorkspace
      sections={sections}
      profile={profile.data}
      clinicSettings={clinicSettings.data}
      intakeForm={intakeForm.data}
      services={services.data ?? []}
      rooms={rooms.data ?? []}
      availability={availability.data}
      canManageServices={canManageServices}
      canManageRooms={canManageRooms}
      canManageAvailability={canManageAvailability}
      isSaving={updateProfile.isPending || updateSettings.isPending || replaceAvailability.isPending}
      onSaveProfile={(value) => updateProfile.mutateAsync(value)}
      onSaveClinicSettings={(value) => updateSettings.mutateAsync(value)}
      onAddService={(value) => addService.mutateAsync(value)}
      onUpdateService={(value) => updateService.mutateAsync(value)}
      onAddRoom={(name) => addRoom.mutateAsync(name)}
      onUpdateRoom={(value) => updateRoom.mutateAsync(value)}
      onSaveAvailability={(value) => replaceAvailability.mutateAsync(value)}
      onCreateLeave={(value) => createLeave.mutateAsync(value)}
      onDecideLeave={(value) => decideLeave.mutateAsync(value)}
      onCancelLeave={(leaveId) => cancelLeave.mutateAsync(leaveId)}
    />
  );
}
